import { useApi } from "@platform/api";
import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";

import { Scenario } from "../pages/BBE/types";

import useToastNotificationService from "./useToastNotificationService";
import { useAddScenarioMutation, useBaselineRenameMutation } from "./useBbe";

type UseScenarioActionsResults = {
  removeScenario: UseMutationResult<unknown, Error, void>;
  duplicateScenario: UseMutationResult<Scenario, Error, { name: string }>;
  renameScenario: (value: string) => void;
  isRenamePending: boolean;
  addScenario: UseMutationResult<
    Scenario,
    Error,
    { name: string; description?: string }
  >;
};

export function useScenarioActions(
  scenarioId: string,
  modelId: string,
  handleCloseModal: () => void,
  isBaseline: boolean = false,
): UseScenarioActionsResults {
  const axios = useApi();
  const queryClient = useQueryClient();
  const { createToastNotification } = useToastNotificationService();

  const invalidateScenarioQueries = (): void => {
    void queryClient.invalidateQueries({
      queryKey: ["bbe", "scenariosSummary", modelId],
    });
    void queryClient.invalidateQueries({
      queryKey: ["bbe", "scenarios", modelId],
    });
  };

  const removeScenario = useMutation({
    mutationFn: () => axios.delete(`/bbe/v1/scenarios/${scenarioId}`),
    onSuccess: () => {
      invalidateScenarioQueries();
      handleCloseModal();
    },
    onError: () => {
      createToastNotification({
        severity: "error",
        message: "Failed to remove scenario. Please try again.",
      });
    },
  });

  const duplicateScenario = useMutation({
    mutationFn: (variables: { name: string }) =>
      axios
        .post<Scenario>(`/bbe/v1/scenarios/${scenarioId}/duplicate`, variables)
        .then((res) => res.data),
    onSuccess: () => {
      invalidateScenarioQueries();
      handleCloseModal();
    },
    onError: () => {
      createToastNotification({
        severity: "error",
        message: "Failed to duplicate scenario. Please try again.",
      });
    },
  });

  const renameBaselineMutation = useBaselineRenameMutation(scenarioId, {
    onSuccess: () => {
      invalidateScenarioQueries();
      void queryClient.invalidateQueries({
        queryKey: ["bbe", "baselines"],
      });
      handleCloseModal();
      createToastNotification({
        severity: "success",
        message: "Baseline renamed successfully.",
      });
    },
    onError: () => {
      createToastNotification({
        severity: "error",
        message: "Failed to rename baseline. Please try again.",
      });
    },
  });

  const renameScenarioMutation = useMutation({
    mutationFn: (name: string) =>
      axios
        .patch<Scenario>(`/bbe/v1/scenarios/${scenarioId}`, { name })
        .then((res) => res.data),
    onSuccess: () => {
      invalidateScenarioQueries();
      handleCloseModal();
      createToastNotification({
        severity: "success",
        message: "Scenario renamed successfully.",
      });
    },
    onError: () => {
      createToastNotification({
        severity: "error",
        message: "Failed to rename scenario. Please try again.",
      });
    },
  });

  const renameScenario = (name: string): void => {
    if (isBaseline) {
      renameBaselineMutation.mutate(name);
    } else {
      renameScenarioMutation.mutate(name);
    }
  };

  const addScenario = useAddScenarioMutation(modelId, {
    onSuccess: handleCloseModal,
  });

  return {
    removeScenario,
    duplicateScenario,
    renameScenario,
    isRenamePending:
      renameScenarioMutation.isPending || renameBaselineMutation.isPending,
    addScenario,
  };
}
